import {
  CreateIssueRequestDto,
  DiscardIssueRequestDto,
} from '@issuemanager/api-client';
import {
  ControlledTextField,
  ExtendedControlledAutocomplete,
} from '@issuemanager/apps/client/components';
import { useBadRequestOrConflictError } from '@issuemanager/apps/client/hooks';
import {
  DEBOUNCE_TIME,
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import { DISCARD_MESSAGE_LENGTH_MAX } from '@issuemanager/constants';
import { debounce } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUsersToNotify } from './use-users-to-notify';

type LocalFields = 'usersToNotify' | 'message';

export interface DiscardIssueFormValues
  extends Omit<DiscardIssueRequestDto, 'usersToNotify'> {
  usersToNotify: {
    id: string;
    label: string;
  }[];
}

const getOptionLabel = (user: { name: string; email: string }) => {
  if (!user.name || user.name.trim() === '') {
    return user.email;
  }
  return `${user.name} <${user.email}>`;
};

export const useDiscardIssueForm = () => {
  const { t } = useTranslation('issue');
  const { errors: serviceErrors, cleanErrors: cleanServiceErrors } =
    useBadRequestOrConflictError();

  const [filterByText, setFilterByText] = useState('');

  const form = useForm<DiscardIssueFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      usersToNotify: [],
      message: '',
    },
  });

  const { loading, options } = useUsersToNotify({
    values: form.getValues('usersToNotify') ?? [],
    filterByText,
  });

  const cleanErrors = () => {
    form.clearErrors();
    cleanServiceErrors();
  };

  const handleTextChange = useMemo(
    () =>
      debounce((string: string) => {
        setFilterByText(string);
      }, DEBOUNCE_TIME),
    [],
  );

  const rules: Record<LocalFields, RegisterOptions<DiscardIssueFormValues>> = {
    usersToNotify: {
      required: t('manage.discard.errors.usersToNotify.required'),
    },
    message: {
      maxLength: {
        value: DISCARD_MESSAGE_LENGTH_MAX,
        message: t('manage.discard.errors.message.maxLength'),
      },
    },
  };

  const labels = {
    usersToNotify: t('manage.discard.labels.sendNotificationsTo'),
    message: t('manage.discard.labels.message'),
  };

  const fields: Record<LocalFields, ReactNode> = {
    usersToNotify: (
      <ExtendedControlledAutocomplete
        name='usersToNotify'
        label={labels.usersToNotify}
        control={form.control}
        rules={rules.usersToNotify}
        multiple
        filterSelectedOptions
        options={options.map((user) => ({
          id: user.id,
          label: getOptionLabel(user),
        }))}
        onTextChange={handleTextChange}
        loading={loading}
      />
    ),
    message: (
      <ControlledTextField
        name='message'
        label={labels.message}
        control={form.control}
        rules={rules.message}
        multiline
        minRows={6}
        maxLength={DISCARD_MESSAGE_LENGTH_MAX}
      />
    ),
  };

  const formStateErrors = getFormStateErrors<CreateIssueRequestDto>(
    mapFormLabelsToFieldLabel(labels),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );

  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    isLoading: false,
    fields,
    cleanErrors,
    errors,
    hasErrors,
    cleanForm: form.reset,
  };
};
